import { MfeReactComponent } from '@carvana/microfrontend-react';
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Blocker, NavigationType } from 'react-router-dom';
import { Step } from '|/app/services';
import telemetryService from '|/app/services/telemetry/telemetry-service';
import { DataDogTeamTags } from '|/app/services/telemetry/telemetry-types';
import ErrorPage from '|/components/base/error';
import Loading from '|/components/base/loading';
import {
  DevicePlatformContext,
  PageEventContext,
  useNavigationBlockContext,
  useSlate
} from '|/context';
import BackstopContext from '|/context/backstop-context';
import PurchaseContext from '|/context/purchase-context';
import { loadVerifxMfe } from '|/hooks/use-global-mfe-config';
import useNavigateAwayFromPurchase from '|/hooks/use-navigate-away-from-purchase';
import useNavigateInPurchase from '|/hooks/use-navigate-in-purchase';
import useNavigateToNextStep from '|/hooks/use-navigate-to-next-step';

const VERIFX_MODULE = 'PurchaseVerificationsModule';
const MFE_LOAD_TIMEOUT_MS = 30000;

const UNBLOCKED_BLOCKER: Blocker = {
  state: 'unblocked',
  proceed: undefined,
  reset: undefined,
  location: undefined
};

const VerifxMfeRoute: React.FC<{ step: Step | null }> = ({ step }) => {
  const { vehicleId } = useContext(PurchaseContext);
  const devicePlatform = useContext(DevicePlatformContext);
  const pageEventContext = useContext(PageEventContext);
  const { isSlateVehicle, isSlateSkin } = useSlate();
  const navigateToNextStep = useNavigateToNextStep();
  const navigateInPurchase = useNavigateInPurchase();
  const navigateAwayFromPurchase = useNavigateAwayFromPurchase();
  const { addBlocker, removeBlocker, addOpenModal, removeOpenModal } = useNavigationBlockContext();
  const { isBackstopTreatment } = useContext(BackstopContext);

  const [mfeBlocker, setMfeBlocker] = useState<Blocker>(UNBLOCKED_BLOCKER);

  const setShouldBlock = useCallback(
    (shouldBlock: boolean) => {
      if (shouldBlock) {
        addBlocker(
          'mfe',
          () => true,
          blocker => {
            // react-router hands back a NEW blocker object on every state change, so the one we push
            // to the MFE stays 'blocked' forever once the user resolves it. The MFE opens its exit
            // warning off `blocker.state === 'blocked'`, so a stale object re-opens that modal on a
            // later render with an already-spent proceed(). Clear it as the MFE resolves.
            const clearThen = (fn?: () => void) => () => {
              setMfeBlocker(UNBLOCKED_BLOCKER);
              fn?.();
            };
            setMfeBlocker({
              ...blocker,
              proceed: clearThen(blocker.proceed),
              reset: clearThen(blocker.reset)
            } as Blocker);
          }
        );
      } else {
        removeBlocker('mfe');
        setMfeBlocker(UNBLOCKED_BLOCKER);
      }
    },
    [addBlocker, removeBlocker]
  );

  // Managed back-close bridge for MFE modals (TXCC-9639) — mirrors the host useBrowserBackClose so
  // Verifx modals register as managed, POP-only blockers and inherit LIFO + the sentinel. The MFE
  // ships separately and cannot be assumed to pair its calls perfectly; addBlocker/removeBlocker are
  // keyed and idempotent, and the coordinator derives the sentinel's lifecycle from that same
  // registry, so an unbalanced call can't drift the two apart.
  const registerBackClose = useCallback(
    (key: string, onClose: () => void) => {
      addBlocker(
        key,
        ({ historyAction }) => historyAction === NavigationType.Pop,
        () => onClose(),
        { managed: true }
      );
    },
    [addBlocker]
  );

  const unregisterBackClose = useCallback(
    (key: string) => {
      removeBlocker(key);
    },
    [removeBlocker]
  );

  // One prop so it is obvious these five belong together. Memoized: the MFE spreads this into
  // MfeNavigationContext, and a fresh object each render would churn the context for every consumer
  // inside the MFE. Not called "backstop" — addOpenModal/removeOpenModal are permanent presence
  // instrumentation that must survive the pp-backstop cleanup.
  const modalNav = useMemo(
    () => ({
      registerBackClose,
      unregisterBackClose,
      addOpenModal,
      removeOpenModal,
      isBackstopTreatment
    }),
    [registerBackClose, unregisterBackClose, addOpenModal, removeOpenModal, isBackstopTreatment]
  );

  useEffect(() => {
    loadVerifxMfe();
  }, []);

  useEffect(() => () => removeBlocker('mfe'), [removeBlocker]);

  return (
    <MfeReactComponent
      module={VERIFX_MODULE}
      component="default"
      step={step}
      vehicleId={vehicleId}
      devicePlatform={devicePlatform}
      source={isSlateVehicle ? 'SLATE' : 'CARVANA'}
      theme={isSlateSkin ? 'SLATE' : 'CARVANA'}
      pageEventContext={pageEventContext}
      onNavigateToNextStep={navigateToNextStep}
      onNavigateInPurchase={navigateInPurchase}
      onNavigateAwayFromPurchase={navigateAwayFromPurchase}
      blocker={mfeBlocker}
      loading={<Loading />}
      setShouldBlock={setShouldBlock}
      modalNav={modalNav}
      fallback={{ Component: <ErrorPage />, timeout: MFE_LOAD_TIMEOUT_MS }}
      onComponentError={({ id, error }: { id: string; error?: Error }) => {
        telemetryService.trackError({
          name: 'verifx-mfe:error',
          error: new Error(`Verifx MFE component failed: ${id}`, { cause: error }),
          context: {
            customType: 'errorBoundary',
            team: DataDogTeamTags.Verifx,
            mfe: VERIFX_MODULE,
            mfeComponentId: id
          }
        });
      }}
    />
  );
};

export default VerifxMfeRoute;
